import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { prop } from "ramda"
import { getCurrentPath, getRecordEditId } from "../../../router/selectors"
import type { ModifiersTableDataItem } from "./ModificationsTable"
import type { StateObject } from "../../../redux/types"
import type { ModifierFormItem } from "./ModifierForm"
import type { CategoryModifier, CategoryModifierResponse, ModifierGroupItemOverride } from "../../../../graphql/types"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import { computeOverriddenValue, applyFiltersToTableData } from "../utils"
import { findById } from "../../../../utils/functional"
import { getItemsCategoriesSearchValue, getModifiersData } from "../selectors"
import { ItemsAndCategoriesTableFilters } from "../types"

export const getModifierById = (state: StateObject, id: Maybe<string>): Maybe<CategoryModifier> => {
    const modifiers: Maybe<CategoryModifierResponse> = getModifiersData(state)

    if (!modifiers || !id) {
        return null
    }

    const modifier: Maybe<CategoryModifier> = findById(id, modifiers.data)

    if (!modifier) {
        return null
    }

    return modifier
}

export const getDataForModifiersTable = (
    state: StateObject,
    filters: ItemsAndCategoriesTableFilters
): ModifiersTableDataItem[] => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const modifiers: Maybe<CategoryModifierResponse> = getModifiersData(state)
    const searchValue = getItemsCategoriesSearchValue(state)

    if (!modifiers) {
        return []
    }

    const data = modifiers.data.map(modifier => {
        const isActiveOverriddenValue = computeOverriddenValue(
            modifiers.overrides,
            selectedLocationId,
            modifier.id,
            prop("isActive")
        )
        return {
            id: modifier.id,
            name: modifier.name,
            groups: modifier.addedToGroups.length,
            isActive: isActiveOverriddenValue != null ? isActiveOverriddenValue : modifier.isActive,
            isActiveOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== modifier.isActive,
            isSelectable: true,
        }
    })
    // @ts-expect-error
    return applyFiltersToTableData(data, searchValue, filters)
}

export const getEditModifierId = (state: StateObject) => getRecordEditId(state)

const buildModifierFormItem = (
    state: StateObject,
    modifier: CategoryModifier,
    modifiers: CategoryModifierResponse
): ModifierFormItem => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const overriddenValue = computeOverriddenValue(
        modifiers.overrides,
        selectedLocationId,
        modifier.id,
        prop("isActive")
    )
    // @ts-expect-error
    const addedToGroupOverriden: Maybe<ModifierGroupItemOverride[]> = computeOverriddenValue(
        modifiers.overrides,
        selectedLocationId,
        modifier.id,
        prop("addedToGroups")
    )
    return {
        modifierId: modifier.id,
        name: modifier.name,
        kitchenName: modifier.kitchenName,
        // @ts-expect-error
        isActive: overriddenValue != null ? overriddenValue : modifier.isActive,
        isActiveOverriden: overriddenValue != null && overriddenValue !== modifier.isActive,
        addedToGroups: modifier.addedToGroups.map(atg => {
            const override = addedToGroupOverriden && addedToGroupOverriden.find(k => k.id === atg.id)
            return {
                groupId: atg.groupId,
                id: atg.id,
                name: atg.name,
                price: {
                    value: override && override.price != null ? override.price : atg.price,
                    override: !!(override && override.price != null),
                },
                groupExtra: {
                    value: override && override.supplement != null ? override.supplement : atg.supplement,
                    override: !!(override && override.supplement != null),
                },
            }
        }),
    }
}

export const getModifierFormData = (state: StateObject): Maybe<ModifierFormItem> => {
    const modifiers: Maybe<CategoryModifierResponse> = getModifiersData(state)
    const editModifierId = getEditModifierId(state)

    if (!modifiers || !editModifierId) {
        return null
    }

    const modifier: Maybe<CategoryModifier> = findById(editModifierId, modifiers.data)

    if (!modifier) {
        return null
    }

    return buildModifierFormItem(state, modifier, modifiers)
}

export const getModifierFormItemById = (state: StateObject, id: Maybe<string>): Maybe<ModifierFormItem> => {
    const modifiers: Maybe<CategoryModifierResponse> = getModifiersData(state)

    if (!modifiers || !id) {
        return null
    }

    const modifier: Maybe<CategoryModifier> = findById(id, modifiers.data)

    if (!modifier) {
        return null
    }

    return buildModifierFormItem(state, modifier, modifiers)
}

export const shouldAddNewModifierToModifierGroup = (state: StateObject) => {
    const currentUrlQuery = getCurrentPath(state).query
    const modifierGroupId = currentUrlQuery.id
    const tab = currentUrlQuery.tab

    if (modifierGroupId) {
        return tab === "modifier-groups"
    }

    return false
}

export const shouldAddNewGroupToItem = (state: StateObject) => {
    const currentUrlQuery = getCurrentPath(state).query
    const tab = currentUrlQuery.tab
    return tab === "categories" || tab === "schematized"
}
